/**
 * FormSheet — shared wrapper for form slide-in sheets in ROROS.
 *
 * Wave 1 of the sheet-migration initiative. Absorbs the ~100 lines of
 * dirty-state-guard + discard-banner + sticky-header-actions + scrollable-body
 * logic that was independently re-implemented in AiOperatorEditor,
 * ActorFormDialog, and ReadinessAssessmentDialog.
 *
 * Responsibilities:
 *   - Open/close lifecycle (delegates to Sheet primitive — NOT replaced)
 *   - Per-field dirty detection via generic `diff` callback
 *   - Per-tab dirty dots (via `tabs[].fields` ∩ diff result)
 *   - Discard-confirm inline banner for all 4 dismiss paths:
 *       close X, Cancel button, backdrop click, Escape key
 *   - Sticky header: title + subtitle + dirty badge + optional extras + Cancel + Save
 *   - Scrollable body
 *   - Optional URL sync via `urlKey` / `urlId` (opt-in, default off)
 *   - Loading state on submit
 *   - Tab management with active-tab state + dirty dots
 *
 * Design contract: /documents/roros-sheet-migration-design-contract.md §4
 * Wave 1 ACs: /documents/roros-sheet-migration-wave-1-phase-0.md
 *
 * Width tiers (§5):
 *   sm = max-w-md  |  md = max-w-3xl (default)  |  lg = max-w-5xl  |  xl = max-w-7xl
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetBody } from '@/components/ui/Sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { AlertTriangle } from 'lucide-react';
import { t } from '@/lib/i18n';

// ─── Public types ──────────────────────────────────────────────────────────────

/**
 * Defines one tab in a tabbed FormSheet.
 *
 * `fields` (optional): which keys of the diff result drive this tab's
 * dirty-dot indicator. FormSheet computes `tabIsDirty = fields.some(f => diff[f])`.
 * If omitted, the tab never shows a dirty dot (useful for read-only tabs).
 *
 * `label` is a ReactNode so consumers can embed custom fragments (e.g. progress
 * counters like "(4/12)") without FormSheet needing to know about them.
 */
export interface TabDef<T extends object> {
  key: string;
  /** Full tab trigger label — may include custom React fragments. */
  label: React.ReactNode;
  /** Which diff-result keys drive the dirty dot for this tab. */
  fields?: Array<keyof T>;
  /** Tab body content. May be a ReactNode or a lazy factory called only when the tab is active. */
  content: React.ReactNode | (() => React.ReactNode);
  /** data-testid for the tab trigger. */
  testId?: string;
}

export interface FormSheetProps<T extends object> {
  // ── Open state ────────────────────────────────────────────────────────
  isOpen: boolean;
  /**
   * Called when the sheet should close. FormSheet routes ALL 4 dismiss
   * paths through its dirty-state guard before forwarding to this callback.
   */
  onClose: () => void;

  // ── Header ────────────────────────────────────────────────────────────
  title: React.ReactNode;
  subtitle?: React.ReactNode;

  // ── Mode — drives default submit label ───────────────────────────────
  mode: 'create' | 'edit';

  // ── Dirty detection ───────────────────────────────────────────────────
  /**
   * Baseline snapshot captured when the sheet opens. Pass `null` before the
   * baseline is ready — FormSheet treats `null` as "not dirty yet".
   */
  baseline: T | null;
  /** Current form state — re-derived from component state on every render. */
  current: T;
  /**
   * Per-field equality check. Returns `true` for each field that differs from
   * baseline. FormSheet computes `isDirty = Object.values(diff(b, c)).some(Boolean)`.
   * Also used for per-tab dirty dots when `tabs[].fields` is provided.
   *
   * // Default comparator: JSON.stringify equality. Deterministic for plain-object snapshots. NOT order-stable for arrays — supply a custom diff if your snapshot contains arrays.
   */
  diff?: (baseline: T, current: T) => Partial<Record<keyof T, boolean>>;

  // ── Submit ────────────────────────────────────────────────────────────
  onSubmit: () => void | Promise<void>;
  isSubmitting: boolean;
  /** Extra conditions that disable the Submit button (e.g. required field empty). */
  submitDisabled?: boolean;
  /** Overrides the mode-based default ("Create" or "Save Changes"). */
  submitLabel?: string;
  /** Label shown while `isSubmitting` is true. Defaults to "Saving…". */
  submittingLabel?: string;

  // ── Optional header extras ────────────────────────────────────────────
  /**
   * Additional ReactNode(s) rendered in the header actions area, BEFORE
   * the Cancel/Save buttons. Use for surface-specific badges (e.g. score).
   */
  headerExtras?: React.ReactNode;

  // ── Layout ────────────────────────────────────────────────────────────
  /** Panel width. Defaults to 'md' (max-w-3xl). */
  width?: 'sm' | 'md' | 'lg' | 'xl';

  // ── URL routing (opt-in) ───────────────────────────────────────────────
  /**
   * When set, FormSheet syncs the sheet's open state with `?urlKey=urlId`
   * in the URL. Opening pushes a history entry; browser Back closes the sheet.
   * Omit to opt out (regression-safe for existing callers).
   */
  urlKey?: string;
  /** The ID value written to the URL param. Required when `urlKey` is set. */
  urlId?: string;

  // ── Tabs (optional) ───────────────────────────────────────────────────
  /** When provided, FormSheet renders a Tabs layout and manages activeTab state. */
  tabs?: Array<TabDef<T>>;
  /** aria-label for the <TabsList>. */
  tabsAriaLabel?: string;

  // ── Body ──────────────────────────────────────────────────────────────
  /**
   * Form body content. When `tabs` is provided, `children` is ignored (tab
   * content is provided via `tabs[].content`). When `tabs` is absent,
   * `children` is rendered directly inside <SheetBody>.
   */
  children?: React.ReactNode;

  // ── Test IDs ──────────────────────────────────────────────────────────
  submitTestId?: string;
}

// ─── Width class map ──────────────────────────────────────────────────────────

const WIDTH_CLASSES: Record<NonNullable<FormSheetProps<object>['width']>, string> = {
  sm: 'md:max-w-md',
  md: 'md:max-w-3xl',
  lg: 'md:max-w-5xl',
  xl: 'md:max-w-7xl',
};

// ─── Component ────────────────────────────────────────────────────────────────

// Default comparator: JSON.stringify equality. Deterministic for plain-object snapshots.
/**
 * Default diff function for FormSheet — deep-equality via JSON.stringify.
 *
 * Returns `{ __default__: true }` when the two snapshots differ, or
 * `{ __default__: false }` when they are equal.
 *
 * ⚠️ Limitations:
 *   - NOT order-stable for arrays — supply a custom diff if your snapshot contains arrays.
 *   - The synthetic `__default__` key satisfies the top-level
 *     `isDirty = Object.values(diff).some(Boolean)` check,
 *     but is **invisible** to `TabDef.fields` dot-checks
 *     (`fields: ['title']` will never light up with this diff).
 *     If your FormSheet uses tabs with `fields:`, supply a custom `diff`
 *     that returns real field keys.
 */
export function defaultDiff<T extends object>(a: T, b: T): Partial<Record<keyof T, boolean>> {
  const changed = JSON.stringify(a) !== JSON.stringify(b);
  // Return a single synthetic key so FormSheet's isDirty check works correctly.
  return { __default__: changed } as unknown as Partial<Record<keyof T, boolean>>;
}

export function FormSheet<T extends object>({
  isOpen,
  onClose,
  title,
  subtitle,
  mode,
  baseline,
  current,
  diff = defaultDiff,
  onSubmit,
  isSubmitting,
  submitDisabled = false,
  submitLabel,
  submittingLabel,
  headerExtras,
  width = 'md',
  urlKey,
  urlId,
  tabs,
  tabsAriaLabel,
  children,
  submitTestId,
}: FormSheetProps<T>) {
  // ── Active tab state ──────────────────────────────────────────────────
  const firstTabKey = tabs?.[0]?.key ?? '';
  const [activeTab, setActiveTab] = useState<string>(firstTabKey);

  // Reset to first tab whenever the sheet opens.
  useEffect(() => {
    if (isOpen && firstTabKey) {
      setActiveTab(firstTabKey);
    }
  }, [isOpen, firstTabKey]);

  // ── Dirty detection ───────────────────────────────────────────────────
  const diffResult: Partial<Record<keyof T, boolean>> =
    baseline != null ? diff(baseline, current) : {};
  const isDirty = Object.values(diffResult).some(Boolean);

  // ── Discard confirm state ─────────────────────────────────────────────
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // Tracks whether the current discard-confirm was triggered by a popstate (browser
  // Back). handleKeepEditing uses this to restore the URL param if the user cancels.
  const popstatePendingRef = useRef(false);

  // Clear discard banner (and popstate flag) whenever the sheet closes.
  useEffect(() => {
    if (!isOpen) {
      setConfirmingDiscard(false);
      popstatePendingRef.current = false;
    }
  }, [isOpen]);

  // ── Dismiss guard — all 4 paths route through this ────────────────────
  const handleDismissAttempt = useCallback(() => {
    if (isSubmitting) return;
    if (isDirty) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  }, [isSubmitting, isDirty, onClose]);

  // Stable ref so the popstate handler (registered in an effect) always calls
  // the latest handleDismissAttempt without needing to re-register.
  const handleDismissAttemptRef = useRef(handleDismissAttempt);
  useEffect(() => { handleDismissAttemptRef.current = handleDismissAttempt; }, [handleDismissAttempt]);

  const handleDiscardConfirm = useCallback(() => {
    popstatePendingRef.current = false;
    setConfirmingDiscard(false);
    onClose();
  }, [onClose]);

  const handleKeepEditing = useCallback(() => {
    setConfirmingDiscard(false);
    // FIX-1: If the dismiss was triggered by browser Back (popstate), the URL
    // param was already removed from the URL. Restore it via pushState so the
    // sheet remains deep-linkable while the user continues editing.
    if (popstatePendingRef.current && urlKey && urlId) {
      const params = new URLSearchParams(window.location.search);
      params.set(urlKey, urlId);
      window.history.pushState(
        null,
        '',
        window.location.pathname + '?' + params.toString(),
      );
    }
    popstatePendingRef.current = false;
  }, [urlKey, urlId]);

  // ── URL routing (opt-in via urlKey) ───────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const urlRoutingActive = !!urlKey;
  // Stable ref to the latest onClose so popstate handler doesn't go stale.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  // Stable ref to urlKey for unmount cleanup (can't rely on closure over prop).
  const urlKeyRef = useRef(urlKey);
  useEffect(() => { urlKeyRef.current = urlKey; }, [urlKey]);
  // Stable ref to urlId for unmount cleanup — used by FIX-3 collision guard.
  const urlIdRef = useRef(urlId);
  useEffect(() => { urlIdRef.current = urlId; }, [urlId]);

  // Unmount cleanup: if the caller unmounts this component immediately on close
  // (conditional rendering) rather than transitioning isOpen→false first, the
  // "isOpen=false" branch of the effect below never runs. This cleanup ensures
  // the URL param is always removed when FormSheet leaves the tree.
  useEffect(() => {
    return () => {
      const key = urlKeyRef.current;
      if (!key) return;
      const params = new URLSearchParams(window.location.search);
      // FIX-3: Only delete the param when its current value matches THIS
      // instance's urlId. Guards against future scenarios where two FormSheets
      // share the same urlKey — unmounting one must not wipe the other's URL state.
      if (params.has(key) && params.get(key) === urlIdRef.current) {
        params.delete(key);
        const newSearch = params.toString();
        // Use history API directly — setSearchParams is not callable post-unmount.
        window.history.replaceState(
          null,
          '',
          window.location.pathname + (newSearch ? '?' + newSearch : ''),
        );
      }
    };
  }, []); // empty deps: cleanup runs exactly once on unmount

  // Sync URL param with isOpen state.
  useEffect(() => {
    if (!urlRoutingActive || !urlId) return;
    if (isOpen) {
      // Push a new history entry so the browser Back button can close the sheet.
      setSearchParams(
        (prev) => {
          if (prev.get(urlKey!) === urlId) return prev; // already set — no-op
          const next = new URLSearchParams(prev);
          next.set(urlKey!, urlId);
          return next;
        },
        { replace: false },
      );
    } else {
      // Sheet closed by the app → remove param (replace so we don't bloat history).
      setSearchParams(
        (prev) => {
          if (!prev.has(urlKey!)) return prev; // already gone — no-op
          const next = new URLSearchParams(prev);
          next.delete(urlKey!);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, urlRoutingActive, urlKey, urlId]);

  // Detect browser Back button: popstate fires → if URL param is gone while
  // sheet is still open → route through the dirty-state guard (FIX-1).
  useEffect(() => {
    if (!urlRoutingActive || !isOpen) return;
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      if (!params.has(urlKey!)) {
        // Browser Back removed the param. Mark as a popstate-triggered dismiss so
        // handleKeepEditing can restore the URL param if the user cancels discard.
        // Then route through handleDismissAttempt so the dirty-state guard fires.
        popstatePendingRef.current = true;
        handleDismissAttemptRef.current();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [urlRoutingActive, urlKey, isOpen]);

  // ── Labels ────────────────────────────────────────────────────────────
  const defaultSubmitLabel = mode === 'create' ? t('Create') : t('Save Changes');
  const effectiveSubmitLabel = submitLabel ?? defaultSubmitLabel;
  const effectiveSubmittingLabel = submittingLabel ?? t('Saving…');

  // ── Dirty badge ───────────────────────────────────────────────────────
  const dirtyBadge = isDirty ? (
    <Badge variant="outline" className="text-warning-700 border-warning-300 bg-warning-50">
      {t('Unsaved changes')}
    </Badge>
  ) : null;

  // ── Header actions ────────────────────────────────────────────────────
  const headerActions = (
    <>
      {headerExtras}
      <Button
        variant="outline"
        size="sm"
        onClick={handleDismissAttempt}
        disabled={isSubmitting}
      >
        {t('Cancel')}
      </Button>
      <Button
        size="sm"
        onClick={onSubmit}
        disabled={isSubmitting || submitDisabled}
        data-testid={submitTestId}
      >
        {isSubmitting ? effectiveSubmittingLabel : effectiveSubmitLabel}
      </Button>
    </>
  );

  // ── Per-tab dirty dot helper ───────────────────────────────────────────
  const tabDirtyDot = (tabKey: string, tabIsDirty: boolean) => {
    if (!tabIsDirty) return null;
    if (activeTab === tabKey) return null;
    return (
      <span
        aria-hidden="true"
        className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-warning-500"
      />
    );
  };

  // ── Body content ──────────────────────────────────────────────────────
  const bodyContent = tabs ? (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList aria-label={tabsAriaLabel} className="mb-4">
        {tabs.map((tab) => {
          const tabIsDirty = tab.fields
            ? tab.fields.some((f) => diffResult[f])
            : false;
          return (
            <TabsTrigger key={tab.key} value={tab.key} data-testid={tab.testId}>
              {tab.label}
              {tabDirtyDot(tab.key, tabIsDirty)}
            </TabsTrigger>
          );
        })}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent key={tab.key} value={tab.key}>
          {/* A-L-3: The `activeTab === tab.key` guard is necessary even though
              TabsContent already returns null for inactive tabs. React evaluates
              JSX children eagerly — without this guard, tab.content() (the lazy
              factory) would be CALLED on every parent render for all tabs, and
              only the returned node would be discarded by TabsContent. The guard
              prevents the factory call entirely for inactive tabs. */}
          {typeof tab.content === 'function'
            ? (activeTab === tab.key ? tab.content() : null)
            : tab.content}
        </TabsContent>
      ))}
    </Tabs>
  ) : (
    children
  );

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Sheet open={isOpen} onClose={handleDismissAttempt}>
      <SheetContent className={WIDTH_CLASSES[width]}>
        <SheetHeader
          title={title}
          subtitle={subtitle}
          badge={dirtyBadge}
          actions={headerActions}
        />

        {/* Discard confirm inline banner — identical across all sheets (Wave 1 contract) */}
        {confirmingDiscard && (
          <div
            role="alertdialog"
            aria-label={t('Discard unsaved changes?')}
            className="flex-shrink-0 flex items-center justify-between gap-4 px-6 py-3 bg-warning-50 border-b border-warning-200 text-warning-900"
          >
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-warning-700" />
              <span className="text-sm font-medium truncate">
                {t('Discard unsaved changes?')}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={handleKeepEditing}>
                {t('Keep editing')}
              </Button>
              <Button variant="danger" size="sm" onClick={handleDiscardConfirm}>
                {t('Discard')}
              </Button>
            </div>
          </div>
        )}

        <SheetBody>{bodyContent}</SheetBody>
      </SheetContent>
    </Sheet>
  );
}
